import { useJsApiLoader } from '@react-google-maps/api';
import { useMemo, useState, useEffect } from 'react';

// IMPORTANT: This must match EXACTLY what's already being used in the app
// The error occurs because different parts of the app are using different options
// Removed 'localContext' as it's causing errors
const GOOGLE_MAPS_LIBRARIES = ['places', 'visualization', 'drawing', 'geometry'];

// Get the API key from environment variables
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Flag to track if we've shown the API key warning
let hasShownApiKeyWarning = false;

// Create a shared loader for Google Maps that includes all required libraries
// IMPORTANT: This must be the single source of truth for Google Maps API loading
// All components must use this hook to ensure consistent library loading
export const useGoogleMapsLoader = () => {
  const [showFallback, setShowFallback] = useState(false);
  
  // Use useMemo to ensure the options object is not recreated on each render
  const options = useMemo(() => ({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    // IMPORTANT: Do not include additional options that might conflict with existing usage
    // The error occurs because the app is already using a different configuration
  }), []);
  
  const result = useJsApiLoader(options);
  
  // Handle missing API key gracefully
  useEffect(() => {
    // Check for API key and show warning only once
    if (!GOOGLE_MAPS_API_KEY && !hasShownApiKeyWarning) {
      console.warn('Google Maps API key is missing. Maps functionality will be limited.');
      hasShownApiKeyWarning = true;
      setShowFallback(true);
    }
    
    // Handle repeated loading failures
    if (result.loadError && !showFallback) {
      console.error('Failed to load Google Maps:', result.loadError);
      setShowFallback(true);
    }
  }, [result.loadError]);
  
  // Return the original result plus a flag indicating if we should use fallback UI
  return {
    ...result,
    showFallback
  };
};

// Export the libraries array for direct use if needed
export const libraries = GOOGLE_MAPS_LIBRARIES;
